import { useMutation, useQuery } from '@apollo/client';
import {
  CANCEL_COMPANY_INVITE,
  ConfirmationModal,
  GET_COMPANY_INVITATIONS,
  GET_COMPANY_TEAM_MEMBERS,
  REMOVE_COMPANY_MEMBER,
  Table,
  Tooltip,
  TRANSFER_COMPANY_OWNERSHIP,
  useAuthUser,
  useCurrentCompany,
} from '@employ-engage/common-components';
import {
  User,
  TableCellContentAlignEnum,
  TableDataRow,
} from '@employ-engage/data';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { InviteModal } from '../invite-modal/invite-modal';

type ExtendedUser = User & {
  name: string;
  email: string;
  role: string;
  [key: string]: any;
};

/**
 * Team members come back with name, firstName and lastName (which are set
 * varies by account), none of which are on the shared `User` type.
 */
type TeamMemberNames = Pick<User, 'email'> & {
  name?: string | null;
  firstName?: string | null;
  lastName?: string | null;
};

const displayNameFor = (member: TeamMemberNames) =>
  member.name ||
  [member.firstName, member.lastName].filter(Boolean).join(' ') ||
  member.email;

export const TeamSettings = () => {
  const { user } = useAuthUser();
  const { currentCompany, refetchCompany, loading: companyLoading } =
    useCurrentCompany();
  const { t } = useTranslation(['common']);

  const [userSelectedForOwner, setUserSelectedForOwner] = useState<User | null>(
    null
  );
  const [userSelectedForRemoval, setUserSelectedForRemoval] =
    useState<User | null>(null);
  const [
    openRemoveMyselfFromCompanyConfirmationModal,
    setOpenRemoveMyselfFromCompanyConfirmationModal,
  ] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [openInviteModal, setOpenInviteModal] = useState(false);
  const [invitationSelectedForDeletion, setInvitationSelectedForDeletion] =
    useState<{ id: string; email: string } | null>(null);

  // `currentCompany` starts as an empty object, so its id is only meaningful
  // once loading is done — skipping on a blank id avoids a guaranteed-failing
  // query on first render.
  const companyId = currentCompany?.id ?? '';
  const ownerId = currentCompany?.ownerId ?? '';

  const {
    data: membersData,
    loading: membersLoading,
    refetch: refetchMembers,
  } = useQuery(GET_COMPANY_TEAM_MEMBERS, {
    variables: { companyId },
    skip: !companyId,
    fetchPolicy: 'cache-and-network',
  });

  const teamMembers: User[] = membersData?.companyTeamMembers ?? [];

  const isOwner = !!ownerId && user?.id === ownerId;

  // Owner-only on the server, so a member asking would simply be refused.
  const { data: invitationsData, refetch: refetchInvitations } = useQuery(
    GET_COMPANY_INVITATIONS,
    { skip: !isOwner, fetchPolicy: 'cache-and-network' }
  );
  const invitations = invitationsData?.companyInvitations ?? [];

  const [cancelInvite] = useMutation(CANCEL_COMPANY_INVITE);

  const cancelInvitation = async () => {
    if (!invitationSelectedForDeletion?.id) return;
    setActionError(null);
    try {
      await cancelInvite({
        variables: { id: invitationSelectedForDeletion.id },
      });
      await refetchInvitations();
    } catch (error: any) {
      console.error('Could not cancel invitation', error);
      setActionError(error?.message ?? t('Something went wrong.'));
    }
    setInvitationSelectedForDeletion(null);
  };

  const [transferOwnership] = useMutation(TRANSFER_COMPANY_OWNERSHIP);
  const [removeMember] = useMutation(REMOVE_COMPANY_MEMBER);

  const makeUserTheOwner = async (selectedUser: User) => {
    if (!selectedUser?.id) return;
    setActionError(null);
    try {
      await transferOwnership({ variables: { userId: selectedUser.id } });
      // The owner badge and every permission on this page read from the
      // company, so it has to be re-read before the table is right.
      await refetchCompany?.();
      await refetchMembers();
    } catch (error: any) {
      console.error('Could not transfer ownership', error);
      setActionError(error?.message ?? t('Something went wrong.'));
    }
    setUserSelectedForOwner(null);
  };

  const removeUserFromCompany = async (userToRemove: User) => {
    if (!userToRemove?.id) return;
    setActionError(null);
    const isSelf = userToRemove.id === user?.id;
    try {
      await removeMember({ variables: { userId: userToRemove.id } });
      if (isSelf) {
        // They no longer belong here; sending them to the root lets the app
        // route them to whatever company they have left, or to sign-in.
        window.location.assign('/');
        return;
      }
      await refetchMembers();
    } catch (error: any) {
      console.error('Could not remove member', error);
      setActionError(error?.message ?? t('Something went wrong.'));
    }
    setUserSelectedForRemoval(null);
    setOpenRemoveMyselfFromCompanyConfirmationModal(false);
  };

  if (companyLoading || (membersLoading && !membersData)) {
    return <div className="py-4 text-sm">{t('Loading…')}</div>;
  }

  return (
    <div className="space-y-2 py-4">
      {actionError && (
        <p className="rounded bg-semantic-error/20 p-2 text-sm" role="alert">
          {actionError}
        </p>
      )}
      <Table
        label={t('Team Members')}
        headerList={[
          { id: 'name', headerLabel: t('Name') },
          { id: 'email', headerLabel: t('Email') },
          {
            id: 'role',
            headerLabel: t('Role'),
            alignment: TableCellContentAlignEnum.CENTER,
          },
          {
            id: 'actions',
            headerLabel: t('Actions'),
            alignment: TableCellContentAlignEnum.CENTER,
          },
        ]}
        dataList={teamMembers}
        mapData={(row: TableDataRow<User>): ExtendedUser => {
          return {
            ...row,
            id: row.id,
            name: displayNameFor(row),
            email: row.email,
            role:
              row.id === ownerId
                ? t('Owner')
                : t('Member'),
            actions: (
              <div className="space-x-1">
                {row.id !== ownerId &&
                user?.id === ownerId ? (
                  <Tooltip label={t('Make Owner')}>
                    <button
                      onClick={() => {
                        setUserSelectedForOwner(row);
                      }}
                    >
                      <i className="fa-solid fa-user-crown text-yellow-500" />
                    </button>
                  </Tooltip>
                ) : null}
                {(user?.id === ownerId ||
                  user?.id === row.id) &&
                row.id !== ownerId ? (
                  <Tooltip
                    label={
                      user?.id === row.id
                        ? t('Leave Company')
                        : t('Remove Member')
                    }
                  >
                    <button
                      onClick={() => {
                        if (user?.id === row.id) {
                          setOpenRemoveMyselfFromCompanyConfirmationModal(true);
                        } else {
                          setUserSelectedForRemoval(row);
                        }
                      }}
                    >
                      <i className="fa-solid fa-trash text-red-500" />
                    </button>
                  </Tooltip>
                ) : null}
              </div>
            ),
          };
        }}
      />
      {/* Invitations are owner-only on the server, so the whole section is
          hidden rather than shown and then refused. */}
      {isOwner && (
        <Table
          label={t('Invites')}
          headerList={[
            { id: 'email', headerLabel: t('Email') },
            { id: 'expiresAt', headerLabel: t('Expires') },
            {
              id: 'actions',
              headerLabel: t('Actions'),
              alignment: TableCellContentAlignEnum.CENTER,
            },
          ]}
          dataList={(invitations ?? []) as never}
          mapData={(row: any) => ({
            ...row,
            email: row.email,
            expiresAt: row.expiresAt
              ? new Date(row.expiresAt).toLocaleDateString()
              : '',
            actions: (
              <Tooltip label={t('Cancel Invite')}>
                <button onClick={() => setInvitationSelectedForDeletion(row)}>
                  <i className="fa-solid fa-trash text-red-500" />
                </button>
              </Tooltip>
            ),
          })}
          actionItems={
            <div className="h-[49px] items-center justify-end gap-3.5 inline-flex">
              <Tooltip label={t('Create Invite')}>
                <button
                  onClick={() => setOpenInviteModal(true)}
                  className="flex items-center justify-start gap-[7.27px] rounded-2xl bg-light-primary p-[14.53px]"
                >
                  <i className="fa-solid fa-plus text-white" />
                  <span className="text-white">{t('Invite')}</span>
                </button>
              </Tooltip>
            </div>
          }
        />
      )}
      <InviteModal
        open={openInviteModal}
        setOpen={setOpenInviteModal}
        onInviteSent={refetchInvitations}
      />
      <ConfirmationModal
        text={t('delete-email-confirmation', {
          email: invitationSelectedForDeletion?.email,
        })}
        open={!!invitationSelectedForDeletion}
        setOpen={() => setInvitationSelectedForDeletion(null)}
        onConfirm={cancelInvitation}
      />
      <ConfirmationModal
        warningText={t(
          'This cannot be undone without the cooperation of the new owner'
        )}
        text={t('make-owner-confirmation', {
          name: userSelectedForOwner?.displayName || '',
        })}
        open={!!userSelectedForOwner}
        setOpen={() => setUserSelectedForOwner(null)}
        onConfirm={() => {
          makeUserTheOwner(userSelectedForOwner as User);
        }}
      />
      <ConfirmationModal
        warningText={t(
          'This cannot be undone. They will need to be re-invited to the company.'
        )}
        text={t('remove-member-confirmation', {
          name: userSelectedForRemoval?.displayName || '',
        })}
        open={!!userSelectedForRemoval}
        setOpen={() => setUserSelectedForRemoval(null)}
        onConfirm={() => {
          removeUserFromCompany(userSelectedForRemoval as User);
        }}
      />
      <ConfirmationModal
        warningText={t(
          'This cannot be undone. You will need to be re-invited to the company.'
        )}
        text={t(`Are you sure you want to leave the company?`)}
        open={openRemoveMyselfFromCompanyConfirmationModal}
        setOpen={setOpenRemoveMyselfFromCompanyConfirmationModal}
        onConfirm={() => {
          removeUserFromCompany(user as User);
        }}
      />
    </div>
  );
};

export default TeamSettings;
